import { TableColumnConfigInterface } from '../table.interface';

export class TableDataMapper<T = any> {
  private _config: Record<string, TableColumnConfigInterface<T>>;

  constructor(config: TableColumnConfigInterface<T>[] | Record<string, TableColumnConfigInterface<T>>) {
    this._config = Array.isArray(config)
      ? config.reduce((acc, column) => {
          acc[column.key as string] = column;
          return acc;
        }, {})
      : config;
  }

  public static readonly mappers = {
    link: (column, row) => ({ ...row, href: this.getHref(column, row) }),
    routerLink: (column, row) => ({ ...row, routerLink: this.getRouterLink(column, row) }),
  };

  public map(data: T[]): T[] {
    const mappers = Object.values(this._config)
      .map((column) => [column, column.mapFn ?? TableDataMapper.mappers[column.type]])
      .filter(([, mapFn]) => !!mapFn);
    if (!mappers.length) return data;

    const mapped = data.map((row) =>
      mappers.reduce(
        (acc, [column, mapFn]) => ({
          ...acc,
          ...mapFn(column, row),
        }),
        {}
      )
    );

    return mapped;
  }

  private static getHref<R = any>(column: TableColumnConfigInterface<R>, row: R): string {
    const fallback = row[column.key] as string;

    if (!column.href) return fallback;

    const searchString = `({(.*?)})+`;
    const searchStringRegex = new RegExp(searchString, 'g');
    if (!searchStringRegex.test(column.href)) return fallback;

    const match = column.href.match(searchStringRegex)[0];
    const key = match.replace(/[{}]/g, '');
    if (!Object.prototype.hasOwnProperty.call(row, key)) return fallback;

    const value = row[key] as string;
    const href = column.href.replace(searchStringRegex, value);

    return href;
  }

  private static getRouterLink<R = any>(column: TableColumnConfigInterface<R>, row: R): string[] {
    const fallback = [];

    if (!column.routerLink) return fallback;

    return column.routerLink.map((segment) => {
      return segment.replace(/\{(\w+)\}/g, (_, key) => {
        return (row[key] as string) ?? '';
      });
    });
  }
}
